import React, { useState, useEffect } from "react";
import { database } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import "./FoglioPresenza.css"; // Reuse existing styles

export default function ValorizationModal({
  show,
  onClose,
  presenze,
  reperibilita,
  documenti,
  spese,
  user,
  monthFilter,
}) {
  const { user: currentUser } = useAuthContext();
  const isAdmin = useIsAdmin(currentUser);

  // State per i bonus confermati
  const [confirmedBonuses, setConfirmedBonuses] = useState(null);

  useEffect(() => {
    if (!user || !monthFilter) return;

    const [yearStr, monthStr] = monthFilter.split("-");
    const year = parseInt(yearStr);
    const monthIndex = parseInt(monthStr) - 1; // 0-indexed
    const docId = `${user.id}_${year}_${monthIndex}`;

    const fetchBonuses = async () => {
      try {
        const doc = await database.collection("bonus_mensili").doc(docId).get();
        if (doc.exists) {
          setConfirmedBonuses(doc.data().bonuses || []);
        } else {
          setConfirmedBonuses(null);
        }
      } catch (e) {
        console.error("Error fetching bonuses", e);
      }
    };

    fetchBonuses();
  }, [user, monthFilter]);

  if (!show || !user) return null;

  // Helper functions (duplicated from presenzeDipendentePdf.js for consistency)
  const toDateObject = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "object" && typeof value.toDate === "function") {
      return value.toDate();
    }
    if (typeof value === "string" || typeof value === "number") {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const isBonusActiveForMonth = (bonus, targetYear, targetMonthIndex) => {
    if (!bonus?.scadenza) return true;
    const d = toDateObject(bonus.scadenza);
    if (!d) return true;
    const scadYear = d.getFullYear();
    const scadMonth = d.getMonth();
    if (scadYear > targetYear) return true;
    if (scadYear < targetYear) return false;
    return scadMonth >= targetMonthIndex;
  };

  const roundEntryTimeToNextHalfHour = (date) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    if (minutes === 0) return rounded;
    if (minutes <= 30) rounded.setMinutes(30, 0, 0);
    else rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
    return rounded;
  };

  const roundExitTimeToPreviousHalfHour = (date) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    if (minutes === 0 || minutes === 30) return rounded;
    if (minutes < 30) rounded.setMinutes(0, 0, 0);
    else rounded.setMinutes(30, 0, 0);
    return rounded;
  };

  const parseTimeString = (timeStr, baseDate) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const m = timeStr.match(/^([0-1]?\d|2[0-3]):([0-5]\d)$/);
    if (!m) return null;
    const d = baseDate instanceof Date ? new Date(baseDate) : new Date();
    d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
    return d;
  };

  // Calculate totals
  const totals = presenze.reduce(
    (acc, item) => {
      const inDate = toDateObject(item.oraIngresso);
      const outDate = toDateObject(item.oraUscita);
      const baseDate = toDateObject(item.dataRegistrazione);
      const inDateResolved =
        inDate ||
        (typeof item.oraIngresso === "string"
          ? parseTimeString(item.oraIngresso, baseDate)
          : null);
      const outDateResolved =
        outDate ||
        (typeof item.oraUscita === "string"
          ? parseTimeString(item.oraUscita, baseDate)
          : null);

      if (
        inDateResolved &&
        outDateResolved &&
        !item.isFerie &&
        !item.isMalattia
      ) {
        const ingressoRounded = roundEntryTimeToNextHalfHour(inDateResolved);
        const uscitaRounded = roundExitTimeToPreviousHalfHour(outDateResolved);
        const diffMs = uscitaRounded - ingressoRounded;
        let diffHours = diffMs / (1000 * 60 * 60);

        if (item.durataPausa && item.durataPausa !== "nessuno") {
          const p = parseFloat(item.durataPausa);
          if (!Number.isNaN(p)) diffHours -= p;
        }
        diffHours = Math.max(0, diffHours);

        if (diffHours > 0) {
          acc.workingDays += 1;
          acc.totalHours += diffHours;
          const d = baseDate;
          const isWeekend = d && (d.getDay() === 0 || d.getDay() === 6);
          if (isWeekend) {
            acc.straordinario += diffHours;
            acc.weekendStraord += diffHours;
          } else {
            if (diffHours <= 8) {
              acc.ordinarie += diffHours;
            } else if (diffHours <= 9) {
              acc.ordinarie += 8;
              acc.viaggio += diffHours - 8;
            } else {
              acc.ordinarie += 8;
              acc.viaggio += 1;
              acc.straordinario += diffHours - 9;
            }
          }
        }
      }
      return acc;
    },
    {
      totalHours: 0,
      workingDays: 0,
      ordinarie: 0,
      viaggio: 0,
      straordinario: 0,
      weekendStraord: 0,
    },
  );

  const anagrafica = user?.anagrafica || {};
  const valPasto = parseFloat(anagrafica.pasto) || 0;
  const valViaggio = parseFloat(anagrafica.oreViaggio) || 0;
  const valStraord = parseFloat(anagrafica.oreStraordinarie) || 0;
  const tipoStipendio = anagrafica.tipoStipendio || "Contratto";

  // Calcolo giorni di reperibilità
  let giorniReperibilita = 0;
  if (reperibilita && reperibilita.length > 0 && monthFilter) {
    const [year, month] = monthFilter.split("-").map(Number);
    // monthFilter è "YYYY-MM"
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);

    reperibilita.forEach((rep) => {
      const repStart = toDateObject(rep.dataInizio);
      const repEnd = toDateObject(rep.dataFine);

      if (repStart && repEnd) {
        // Intersezione tra periodo reperibilità e mese selezionato
        const start = repStart < monthStart ? monthStart : repStart;
        const end = repEnd > monthEnd ? monthEnd : repEnd;

        if (start <= end) {
          // Calcolo giorni (inclusivo)
          const s = new Date(start);
          s.setHours(0, 0, 0, 0);
          const e = new Date(end);
          e.setHours(0, 0, 0, 0);

          const diffTime = Math.abs(e - s);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          giorniReperibilita += diffDays;
        }
      }
    });
  }
  const importoReperibilita = giorniReperibilita * 10;

  // Calcolo Bonus
  let importoBonus = 0;
  let activeBonuses = [];

  const [targetYearStr, targetMonthStr] = monthFilter.split("-");
  const targetYear = parseInt(targetYearStr, 10);
  const targetMonthIndex = parseInt(targetMonthStr, 10) - 1;

  if (confirmedBonuses !== null) {
    // Usa i bonus confermati dal database
    confirmedBonuses
      .filter((b) => isBonusActiveForMonth(b, targetYear, targetMonthIndex))
      .forEach((b) => {
        const val = parseFloat(b.importo);
        if (!isNaN(val)) {
          importoBonus += val;
          activeBonuses.push(b);
        }
      });
  } else if (anagrafica.bonus && Array.isArray(anagrafica.bonus)) {
    // Fallback: calcolo automatico da anagrafica
    anagrafica.bonus.forEach((b) => {
      if (isBonusActiveForMonth(b, targetYear, targetMonthIndex)) {
        const val = parseFloat(b.importo);
        if (!isNaN(val)) {
          importoBonus += val;
          activeBonuses.push(b);
        }
      }
    });
  }

  // Calcolo Addebiti
  let importoAddebiti = 0;
  const activeAddebiti = [];
  if (documenti && Array.isArray(documenti) && monthFilter) {
    const [yearStr, monthStr] = monthFilter.split("-");
    const months = [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre",
    ];
    const monthName = months[parseInt(monthStr, 10) - 1];

    // console.log("Filtering Addebiti:", {
    //   yearStr,
    //   monthName,
    //   totalDocs: documenti.length,
    // });

    documenti.forEach((doc) => {
      // Per compatibilità: alcuni addebiti usano campi stringa anno/competenza,
      // altri potrebbero usare un timestamp. Manteniamo la logica originale
      // che si basa su "competenza" (nome mese) e "anno" (stringa).
      // AGGIORNAMENTO: controllo anche la data se disponibile per maggiore robustezza
      let isSameMonth = false;

      // Check 1: string match (legacy) - Relaxed equality check
      if (
        String(doc.anno) === yearStr &&
        doc.competenza &&
        doc.competenza.toLowerCase() === monthName.toLowerCase()
      ) {
        isSameMonth = true;
      }
      // Check 2: date object match (se disponibile e non già matchato)
      else if (doc.data) {
        const docDate = toDateObject(doc.data);
        if (
          docDate &&
          docDate.getFullYear() === parseInt(yearStr, 10) &&
          docDate.getMonth() === parseInt(monthStr, 10) - 1
        ) {
          isSameMonth = true;
        }
      }

      if (doc.tipoDocumento === "addebitoPersonale" && doc.owner === user.id) {
        if (isSameMonth) {
          const val = parseFloat(doc.importo);
          if (!isNaN(val)) {
            importoAddebiti += val;
            activeAddebiti.push(doc);
          }
        }
      }
    });
  }

  // Calcolo Note Spese
  let importoNoteSpese = 0;
  const activeNoteSpese = [];
  if (spese && Array.isArray(spese) && monthFilter) {
    const [yearStr, monthStr] = monthFilter.split("-");
    const targetYear = parseInt(yearStr, 10);
    const targetMonth = parseInt(monthStr, 10) - 1; // 0-indexed

    // console.log("Filtering Spese:", {
    //   targetYear,
    //   targetMonth,
    //   totalSpese: spese.length,
    // });

    spese.forEach((spesa) => {
      // Check autore: può essere oggetto {id: ...} o stringa id
      const spesaAutoreId = spesa.autore?.id || spesa.autore;
      const userMatch = spesaAutoreId === user.id;

      // Check pagamento type
      // Nota: verifica se il campo è 'pagamento' o 'metodoPagamento' o altro
      // User dice: proprietà "pagamento":"Nota Spese"
      const typeMatch =
        spesa.pagamento && spesa.pagamento.toLowerCase() === "nota spese";

      // Check date
      let dateMatch = false;
      const spesaDate = toDateObject(spesa.data);
      if (
        spesaDate &&
        spesaDate.getFullYear() === targetYear &&
        spesaDate.getMonth() === targetMonth
      ) {
        dateMatch = true;
      }

      // FALLBACK: se non matcha per data, controlla se esistono campi competenza/anno espliciti
      // (utile se la spesa è stata caricata con una data diversa ma assegnata a un mese specifico)
      if (!dateMatch && spesa.competenza && spesa.anno) {
        const months = [
          "gennaio",
          "febbraio",
          "marzo",
          "aprile",
          "maggio",
          "giugno",
          "luglio",
          "agosto",
          "settembre",
          "ottobre",
          "novembre",
          "dicembre",
        ];
        const targetMonthName = months[targetMonth];
        // Normalizza stringhe per confronto case-insensitive
        if (
          String(spesa.anno) === String(targetYear) &&
          String(spesa.competenza).toLowerCase() === targetMonthName
        ) {
          dateMatch = true;
        }
      }

      if (userMatch && typeMatch) {
        if (dateMatch) {
          const val = parseFloat(spesa.importo);
          if (!isNaN(val)) {
            importoNoteSpese += val;
            activeNoteSpese.push(spesa);
          }
        } else {
          // Debug log
          // console.log("Spesa skipped (date mismatch):", { ... });
        }
      } else if (!userMatch && typeMatch) {
        // Debug log per utente mismatch
        // console.log("Spesa skipped (user mismatch):", spesaAutoreId, user.id);
      }
    });
  }

  const importoPasti = totals.workingDays * valPasto;
  const importoViaggio = totals.viaggio * valViaggio;
  const importoStraord = totals.straordinario * valStraord;
  const importoStraordWE = (totals.weekendStraord || 0) * valStraord;
  // const importoFisso = parseFloat(anagrafica.importoFisso) || 0;

  let totaleImporti = 0;
  let details = [];

  const formatEuro = (val) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(val);

  if (tipoStipendio === "Fisso") {
    totaleImporti = importoReperibilita + importoBonus - importoAddebiti;
    details = [];
  } else if (tipoStipendio === "Fisso+Sabato") {
    totaleImporti =
      importoStraordWE + importoReperibilita + importoBonus - importoAddebiti;
    details = [
      {
        label: "Straordinari Weekend",
        value: importoStraordWE,
        quantity: `${totals.weekendStraord.toFixed(2)}h`,
        desc: `${totals.weekendStraord.toFixed(2)}h x ${formatEuro(valStraord)}`,
      },
    ];
  } else if (tipoStipendio === "Fisso+Straordinari") {
    totaleImporti =
      importoPasti +
      importoViaggio +
      importoStraord +
      importoReperibilita +
      importoBonus -
      importoAddebiti;
    details = [
      {
        label: "Pasti",
        value: importoPasti,
        quantity: `${totals.workingDays} gg`,
        desc: `${totals.workingDays} gg x ${formatEuro(valPasto)}`,
      },
      {
        label: "Viaggio",
        value: importoViaggio,
        quantity: `${totals.viaggio.toFixed(2)}h`,
        desc: `${totals.viaggio.toFixed(2)}h x ${formatEuro(valViaggio)}`,
      },
      {
        label: "Straordinari",
        value: importoStraord,
        quantity: `${totals.straordinario.toFixed(2)}h`,
        desc: `${totals.straordinario.toFixed(2)}h x ${formatEuro(valStraord)}`,
      },
    ];
  } else {
    // Contratto (Default)
    totaleImporti =
      importoPasti +
      importoViaggio +
      importoStraord +
      importoReperibilita +
      importoBonus -
      importoAddebiti;
    details = [
      {
        label: "Pasti",
        value: importoPasti,
        quantity: `${totals.workingDays} gg`,
        desc: `${totals.workingDays} gg x ${formatEuro(valPasto)}`,
      },
      {
        label: "Viaggio",
        value: importoViaggio,
        quantity: `${totals.viaggio.toFixed(2)}h`,
        desc: `${totals.viaggio.toFixed(2)}h x ${formatEuro(valViaggio)}`,
      },
      {
        label: "Straordinari",
        value: importoStraord,
        quantity: `${totals.straordinario.toFixed(2)}h`,
        desc: `${totals.straordinario.toFixed(2)}h x ${formatEuro(valStraord)}`,
      },
    ];
  }

  // Aggiungi Reperibilità se presente
  if (importoReperibilita > 0) {
    details.push({
      label: "Reperibilità",
      value: importoReperibilita,
      quantity: `${giorniReperibilita} gg`,
      desc: `${giorniReperibilita} gg x ${formatEuro(10)}`,
    });
  }

  // Aggiungi Bonus se presente
  if (importoBonus > 0) {
    details.push({
      label: "Bonus",
      value: importoBonus,
      quantity: activeBonuses.map((b) => b.nome).join(", "),
      desc: activeBonuses.map((b) => b.nome).join(", "),
    });
  }

  // Aggiungi Addebiti se presente
  if (importoAddebiti > 0) {
    details.push({
      label: "Addebiti",
      value: -importoAddebiti,
      quantity: activeAddebiti
        .map((d) => d.identificativo || "Addebito")
        .join(", "),
      desc: activeAddebiti
        .map((d) => d.identificativo || "Addebito")
        .join(", "),
    });
  }

  // Aggiungi Note Spese se presente
  if (importoNoteSpese > 0) {
    totaleImporti += importoNoteSpese;
    details.push({
      label: "Note Spese",
      value: importoNoteSpese,
      quantity: `${activeNoteSpese.length} voci`,
      desc: activeNoteSpese.map((s) => s.nome || "Spesa").join(", "),
    });
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Riepilogo Valorizzazione Ore</h3>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="presence-form">
          <div
            style={{
              marginBottom: "20px",
              paddingBottom: "15px",
              borderBottom: "1px solid #eee",
            }}
          >
            <p>
              <strong>Dipendente:</strong> {user.displayName}
            </p>
            <p>
              <strong>Mese:</strong> {monthFilter}
            </p>
            <p>
              <strong>Tipo Contratto:</strong> {tipoStipendio}
            </p>
          </div>

          <h4 style={{ margin: "0 0 10px 0", color: "#666" }}>Dettaglio</h4>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              overflow: "hidden",
              marginBottom: "20px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {details
                  .filter((item) => item.value !== 0)
                  .map((item, index) => (
                    <tr
                      key={index}
                      style={{ borderBottom: "1px solid #e9ecef" }}
                    >
                      <td style={{ padding: "12px 15px" }}>
                        <div style={{ fontWeight: "600", color: "#333" }}>
                          {item.label}
                        </div>
                        {isAdmin && item.desc && (
                          <div
                            style={{ fontSize: "0.85rem", color: "#6c757d" }}
                          >
                            {item.desc}
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "12px 15px",
                          textAlign: "right",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {isAdmin ? formatEuro(item.value) : item.quantity}
                      </td>
                    </tr>
                  ))}
                {isAdmin && (
                  <tr style={{ backgroundColor: "#e9ecef" }}>
                    <td
                      style={{
                        padding: "12px 15px",
                        fontWeight: "bold",
                        color: "#333",
                      }}
                    >
                      TOTALE RIMBORSI
                    </td>
                    <td
                      style={{
                        padding: "12px 15px",
                        textAlign: "right",
                        fontWeight: "bold",
                        color: "#28a745",
                        fontSize: "1.2rem",
                      }}
                    >
                      {formatEuro(totaleImporti)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h4 style={{ margin: "0 0 10px 0", color: "#666" }}>
            Riepilogo Ore Lavorate
          </h4>
          <div
            className="summary-cards"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
            }}
          >
            <div
              className="summary-card"
              style={{ padding: "15px", borderLeftColor: "#4caf50" }}
            >
              <h5
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "0.9rem",
                  color: "#666",
                }}
              >
                Ordinarie
              </h5>
              <div
                className="summary-number"
                style={{ fontSize: "1.5rem", color: "#4caf50" }}
              >
                {totals.ordinarie.toFixed(1)}
              </div>
            </div>
            <div
              className="summary-card"
              style={{ padding: "15px", borderLeftColor: "#2196f3" }}
            >
              <h5
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "0.9rem",
                  color: "#666",
                }}
              >
                Viaggio
              </h5>
              <div
                className="summary-number"
                style={{ fontSize: "1.5rem", color: "#2196f3" }}
              >
                {totals.viaggio.toFixed(1)}
              </div>
            </div>
            {(totals.straordinario > 0 || totals.weekendStraord > 0) && (
              <div
                className="summary-card"
                style={{ padding: "15px", borderLeftColor: "#f44336" }}
              >
                <h5
                  style={{
                    margin: "0 0 5px 0",
                    fontSize: "0.9rem",
                    color: "#666",
                  }}
                >
                  Straordinari
                </h5>
                <div
                  className="summary-number"
                  style={{ fontSize: "1.5rem", color: "#f44336" }}
                >
                  {totals.straordinario.toFixed(1)}
                </div>
                <div className="summary-detail" style={{ fontSize: "0.75rem" }}>
                  (di cui {totals.weekendStraord.toFixed(1)} nel weekend)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
